// src/pages/purchase/GRNDetail.jsx
import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { canEnterBills } from "../../services/purchaseBillService";
import { useParams, useNavigate } from "react-router-dom";

export default function GRNDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [grn,     setGrn]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [bill,    setBill]    = useState(null);   // D-291 P3d-5a
  const me = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    const fetch = async () => {
      try {
        const user      = JSON.parse(localStorage.getItem("user") || "{}");
        const companyId = user.company_id || "";

        // ✅ Constitution: Material Receive SOT = purchase_receipts
        const snap = await getDocs(query(
          collection(db, "purchase_receipts"),
          where("company_id",        "==", companyId),
          where("purchase_order_id", "==", id)
        ));

        if (!snap.empty) {
          const g = { id: snap.docs[0].id, ...snap.docs[0].data() };
          setGrn(g);
          // D-291 P3d-5a: the vendor's bill entered for this receipt
          if (g.purchase_bill_id) {
            try {
              const bSnap = await getDoc(doc(db, "purchase_bills", g.purchase_bill_id));
              const b = bSnap.exists() ? { id: bSnap.id, ...bSnap.data() } : null;
              setBill(b && b.company_id === companyId ? b : null);
            } catch (e) {
              console.warn("GRN bill fetch failed (non-blocking):", e);
            }
          }
        }
      } catch (err) {
        console.error("GRN fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  if (loading) return <div className="p-6 text-slate-400 text-sm">Loading GRN...</div>;
  if (!grn)    return (
    <div className="p-6">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 mb-4 block">← Back</button>
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center text-red-700">
        GRN not found for this PO.
      </div>
    </div>
  );

  // ✅ Constitution SOT: grand_total purchase_receipts document se lo
  // Items se calculate karna = duplicate logic = FORBIDDEN
  const grandTotal = Number(grn.grand_total || 0);

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 mb-4 block">← Back</button>

      {/* HEADER */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mb-4">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-slate-800">GRN Receipt</h2>
            <p className="text-xs text-slate-400 mt-0.5">PO: {grn.po_number || "—"}</p>
          </div>
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${
            grn.receipt_type === "short_close"
              ? "bg-amber-100 text-amber-700"
              : "bg-green-100 text-green-700"
          }`}>
            {grn.receipt_type === "short_close" ? "Short Closed" : "Full Receipt"}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
          <div>
            <span className="text-slate-400 text-xs block">Vendor</span>
            <span className="font-semibold text-slate-700">{grn.vendor_name || "—"}</span>
          </div>
          <div>
            <span className="text-slate-400 text-xs block">GRN Date</span>
            <span className="font-semibold text-slate-700">
              {grn.created_at?.seconds
                ? new Date(grn.created_at.seconds * 1000).toLocaleDateString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric"
                  })
                : "—"}
            </span>
          </div>
          <div>
            <span className="text-slate-400 text-xs block">Received By</span>
            <span className="font-semibold text-slate-700">{grn.completed_by_name || "—"}</span>
          </div>
          {grn.short_close_reason && grn.receipt_type === "short_close" && (
            <div>
              <span className="text-slate-400 text-xs block">Short Close Reason</span>
              <span className="font-semibold text-amber-700">{grn.short_close_reason}</span>
            </div>
          )}
        </div>
      </div>

      {/* VENDOR BILL - D-291 P3d-5a: goods and bill are two events */}
      {grn.bill_status === "awaiting_bill" && !grn.purchase_bill_id && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-amber-900">Waiting for the vendor's bill</p>
            <p className="text-xs text-amber-800">The bill adds this delivery to the vendor's khaata.</p>
          </div>
          {canEnterBills(me) && (
            <button onClick={() => navigate(`/purchase-bills/new/${grn.id}`)}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white">
              Enter bill
            </button>
          )}
        </div>
      )}
      {grn.bill_status === "billed" && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
          {/* P3d-5b-1: order-last keeps the button on the right of the text. This route is not role-gated (N1) - the button is. */}
          {bill && canEnterBills(me) && (
            <button onClick={() => navigate(`/purchase-bills/${bill.id}`)} className="order-last px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white">
              View bill
            </button>
          )}
          <p className="text-sm font-semibold text-green-900">
            Bill entered
            {bill && ` · ${bill.bill_type === "proforma" ? "Proforma (tax invoice pending)" : `Tax invoice ${bill.vendor_invoice_no || ""}`} · ₹${Number(bill.bill_total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
          </p>
        </div>
      )}
      {grn.bill_status === "not_needed" && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-4 text-sm text-slate-600">
          Nothing was received, so no vendor bill is needed.
        </div>
      )}

      {/* ITEMS */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mb-4">
        <h3 className="font-semibold text-slate-700 mb-3">Items Received</h3>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b">
                <th className="text-left pb-2">Item</th>
                <th className="text-right pb-2">Ordered</th>
                <th className="text-right pb-2">Received</th>
                <th className="text-right pb-2">Cancelled</th>
                <th className="text-right pb-2">Rate</th>
                <th className="text-right pb-2">Amount</th>
                <th className="text-right pb-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {(grn.items || []).map((item, i) => {
                const amount = Number(item.received_qty || 0) * Number(item.rate || 0);
                return (
                  <tr key={i} className="py-2">
                    <td className="py-2.5 pr-3">
                      <div className="font-medium text-slate-800">{item.item_name}</div>
                      <div className="text-xs text-slate-400">{item.unit || "nos"}</div>
                    </td>
                    <td className="text-right py-2.5 text-slate-500">{item.ordered_qty || 0}</td>
                    <td className="text-right py-2.5 font-semibold text-slate-800">{item.received_qty || 0}</td>
                    <td className="text-right py-2.5 text-red-600">{item.cancelled_qty || 0}</td>
                    <td className="text-right py-2.5 text-slate-600">₹{Number(item.rate || 0).toLocaleString()}</td>
                    <td className="text-right py-2.5 font-semibold text-slate-800">₹{amount.toLocaleString()}</td>
                    <td className="text-right py-2.5">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        item.item_grn_status === "exact"  ? "bg-green-100 text-green-700"  :
                        item.item_grn_status === "short"  ? "bg-amber-100 text-amber-700"  :
                        item.item_grn_status === "over"   ? "bg-red-100 text-red-700"      :
                        "bg-slate-100 text-slate-600"
                      }`}>
                        {item.item_grn_status || "—"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* GRAND TOTAL */}
        <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-100">
          <span className="font-semibold text-slate-700">Received value (GRN)</span>
          <span className="text-lg font-black text-slate-800">
            ₹{grandTotal.toLocaleString("en-IN")}
          </span>
        </div>
      </div>
    </div>
  );
}